import { useDispatch, useSelector } from 'react-redux';
import {
  dataLinkDocumentDeSelectedAction,
  dataLinkDocumentSelectedAction
} from '../../../../redux/actions/datalinks';
import { conditionMatches } from '../../../conditions/utils';
import useParse from '../../../parse';

const useSelectDocument = () => {
  const dispatch = useDispatch();

  const { parse } = useParse();
  const user = useSelector(state => state.User);

  const selectDocument = async ({
    value,
    page,
    step,
    documents,
    /*document,*/
    history,
    selections,
    local,
    selectedDocumentId,
    data,
    params
  }) => {
    let documentId;

    if (
      !step?.selectDocumentDataLinkId ||
      step?.selectDocumentDataLinkId?.trim().length === 0
    ) {
      return {
        error: 'a data link id must be supplied with a selectDocument step'
      };
    }

    if (step.selectDocumentBy === 'step') {
      const inputStep = history?.find(
        sequenceItem =>
          sequenceItem.step?.id === step.previousStepForDocumentSelection
      );

      if (!inputStep) {
        return {
          error: 'No previous step has been provided as the input to this step.'
        };
      } else if (!inputStep?.result?.id) {
        return {
          error:
            'The previous step does not have an id field in its result object.'
        };
      }
      documentId = inputStep.result.id;
    } else if (step.selectDocumentBy === 'item') {
      if (!selectedDocumentId && !value) {
        return {
          error:
            'The step was not supplied with a selectedDocumentId. Did you add a "data-documentId" attribute to the element that was clicked?'
        };
      }
      documentId = selectedDocumentId || value;
    } else if (step.selectDocumentBy === 'fields') {
      if (!step.fields || step.fields?.length === 0) {
        return {
          error:
            'The step was not supplied with any fields to select the document against'
        };
      }

      if (
        !documents ||
        !documents[step.selectDocumentDataLinkId]?.documents ||
        !documents[step.selectDocumentDataLinkId]?.documents?.length === 0
      ) {
        return {
          error: `data link id ${step.selectDocumentDataLinkId} has no documents to select from`
        };
      }

      let matchedDocIds = [];
      await Promise.all(
        documents[step.selectDocumentDataLinkId].documents.map(doc => {
          const allFieldsMatched = step.fields.every(field => {
            const condition = {
              dataLinkId: field.dataLinkId,
              field:
                field.name?.trim().length > 0
                  ? field.name
                  : field.dynamicFieldName,
              operator: field.operator,
              value: field.value
            };

            const pageDataLink = page.dataLinks.find(
              d => d.dataLinkId === step.selectDocumentDataLinkId
            );

            const contentType = pageDataLink.dataLink.contentType;

            if (contentType.error) {
              return false;
            }

            condition.value = parse({
              markup: condition.value,
              documents,
              selections,
              user,
              local,
              actionSequenceExecutionHistory: history,
              data,
              params
            });

            const cond = { ...condition };
            delete cond.dataLinkId;

            return conditionMatches({
              condition: cond,
              document: doc,
              contentType
            });
          });

          if (allFieldsMatched) {
            matchedDocIds.push(doc.id);
          }

          return allFieldsMatched;
        })
      );

      // Remove duplicates
      matchedDocIds = [...new Set(matchedDocIds)];
      if (matchedDocIds.length === 1) {
        documentId = matchedDocIds[0];
      } else {
        return {
          error: `${matchedDocIds.length} documents were found while evaluating field conditions for select the document. No single document was matched.`
        };
      }
    } else if (step.selectDocumentBy === 'next') {
      if (documents[step.selectDocumentDataLinkId].documents.length === 0) {
        return {
          error: `data link with id ${step.selectDocumentDataLinkId} contains 0 documents. A next document cannot be selected.`
        };
      }

      const currentDocumentId =
        selections[step.selectDocumentDataLinkId]?.documentId;

      const documentIndex = currentDocumentId
        ? documents[step.selectDocumentDataLinkId].documents.findIndex(
            d => d.id === currentDocumentId
          )
        : -1;

      documentId =
        documentIndex <
        documents[step.selectDocumentDataLinkId].documents.length - 1
          ? documents[step.selectDocumentDataLinkId].documents[
              documentIndex + 1
            ].id
          : documents[step.selectDocumentDataLinkId].documents[documentIndex]
              .id;
    } else if (step.selectDocumentBy === 'previous') {
      const totalDocuments =
        documents[step.selectDocumentDataLinkId].documents.length;
      if (totalDocuments === 0) {
        return {
          error: `datasource with id ${step.selectDocumentDataLinkId} contains 0 documents. A previous document cannot be selected.`
        };
      }

      const currentDocumentId =
        selections[step.selectDocumentDataLinkId]?.documentId;

      const documentIndex = currentDocumentId
        ? documents[step.selectDocumentDataLinkId].documents.findIndex(
            d => d.id === currentDocumentId
          )
        : -1;

      if (documentIndex === -1) {
        documentId = documents[step.selectDocumentDataLinkId].documents[0].id;
      } else {
        documentId =
          documentIndex > totalDocuments - 1
            ? documents[step.selectDocumentDataLinkId].documents[documentIndex]
                .id
            : documents[step.selectDocumentDataLinkId].documents[
                documentIndex === 0 ? 0 : documentIndex - 1
              ].id;
      }
    } else if (step.selectDocumentBy === 'first') {
      if (documents[step.selectDocumentDataLinkId].documents.length === 0) {
        return {
          error: `datasource with id ${step.selectDocumentDataLinkId} contains 0 documents. The first document cannot be selected.`
        };
      }

      documentId = documents[step.selectDocumentDataLinkId].documents[0].id;
    } else if (step.selectDocumentBy === 'last') {
      const totalDocuments =
        documents[step.selectDocumentDataLinkId].documents.length;
      if (totalDocuments === 0) {
        return {
          error: `datasource with id ${step.selectDocumentDataLinkId} contains 0 documents. The last document cannot be selected.`
        };
      }

      documentId =
        documents[step.selectDocumentDataLinkId].documents[totalDocuments - 1]
          .id;
    } else if (step.selectDocumentBy === 'url_parameter') {
      const doc = documents[step.selectDocumentDataLinkId]?.documents.find(d =>
        d[step.selectDocumentDataLinkField].toLowerCase
          ? d[step.selectDocumentDataLinkField].toLowerCase().trim() ===
            params[step.selectDocumentUrlParameterName]?.toLowerCase().trim()
          : d[step.selectDocumentDataLinkField] ===
            params[step.selectDocumentUrlParameterName]
      );
      documentId = doc?.id;
    } else {
      return {
        error: 'The step was not setup correctly and cannot run.'
      };
    }

    const payload = {
      dataLinkId: step.selectDocumentDataLinkId,
      documentId
    };

    selections[step.selectDocumentDataLinkId] = payload;

    dispatch(dataLinkDocumentSelectedAction(payload));

    return payload;
  };

  const deselectDocument = async ({ step, selections }) => {
    if (
      !step?.selectDocumentDataLinkId ||
      step?.selectDocumentDataLinkId?.trim().length === 0
    ) {
      return {
        error: 'a data link id must be supplied with a selectDocument step'
      };
    }

    delete selections[step.selectDocumentDataLinkId];
    dispatch(
      dataLinkDocumentDeSelectedAction({
        dataLinkId: step.selectDocumentDataLinkId
      })
    );

    return {
      success: true,
      message: `documents for datalinkId ${step.selectDocumentDataLinkId} were deselected`
    };
  };

  return {
    selectDocument,
    deselectDocument
  };
};

export default useSelectDocument;
